import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import type { AppDispatch } from "@/app/store";
import { authService } from "@/modules/auth/services/authService";
import { updateStepUpState } from "@/modules/auth/store/authSlice";
import useTranslations from "@/shared/lang/useTranslations";
import { SecurityPinService } from "@/shared/services/securityPin.service";
import { readApiCode, readHumanErrorMessage } from "@/shared/utils/apiError";
import { pickStepUpState } from "@/shared/utils/stepUp";

/** Where a refusal belongs on screen: under the PIN, under the password, or above the whole form. */
export interface SecurityPinFailure {
  field: "pin" | "password" | null;
  message: string;
}

export interface UseSecurityPinResult {
  /** Creates or replaces the PIN. Resolves with the refusal to show, or null once it is saved. */
  save: (pin: string, password: string) => Promise<SecurityPinFailure | null>;
  remove: (password: string) => Promise<SecurityPinFailure | null>;
  /** Re-reads the step-up state (method and PIN lock) after a challenge was refused. Never throws. */
  refresh: () => Promise<void>;
  saving: boolean;
}

/**
 * The caller's own security PIN: create, replace (also the way out of a lock), remove. Every success merges
 * the server's step-up state into the signed-in user, so each pre-check and prompt reads the new method at
 * once.
 */
export function useSecurityPin(): UseSecurityPinResult {
  const { t } = useTranslations("global");
  const dispatch = useDispatch<AppDispatch>();
  const [saving, setSaving] = useState(false);

  const failureOf = useCallback(
    (error: unknown): SecurityPinFailure => {
      const code = readApiCode(error);
      if (code === "PASSWORD_INCORRECT") {
        // Wrong current passwords count toward the login lock, so the person is told before the fifth one.
        return { field: "password", message: t("reauth_password_incorrect_hint") };
      }
      if (code === "PASSWORD_REQUIRED") {
        return { field: "password", message: t("security_pin_password_required") };
      }
      if (code === "SECURITY_PIN_REUSED") {
        return { field: "pin", message: t("security_pin_error_reused") };
      }
      if (code === "SECURITY_PIN_WEAK") {
        // The server names the reason in the person's language; this mirror may not know it yet.
        return { field: "pin", message: readHumanErrorMessage(error, t("security_pin_issue_common")) };
      }
      return { field: null, message: readHumanErrorMessage(error, t("security_pin_error_generic")) };
    },
    [t],
  );

  const save = useCallback(
    async (pin: string, password: string) => {
      setSaving(true);
      try {
        const state = await SecurityPinService.set({ pin, current_password: password });
        dispatch(updateStepUpState(state));
        return null;
      } catch (error: unknown) {
        return failureOf(error);
      } finally {
        setSaving(false);
      }
    },
    [dispatch, failureOf],
  );

  const remove = useCallback(
    async (password: string) => {
      setSaving(true);
      try {
        const state = await SecurityPinService.remove({ current_password: password });
        dispatch(updateStepUpState(state));
        return null;
      } catch (error: unknown) {
        return failureOf(error);
      } finally {
        setSaving(false);
      }
    },
    [dispatch, failureOf],
  );

  const refresh = useCallback(async () => {
    try {
      const me = await authService.getMe();
      dispatch(updateStepUpState(pickStepUpState(me)));
    } catch {
      // The challenge already shows the refusal it got; a lock it could not read is only a missing countdown.
    }
  }, [dispatch]);

  return { save, remove, refresh, saving };
}
